// ── Slack Bot Scope Registry ─────────────────────────────────────────────────
// Canonical registry of Slack bot token scopes with metadata for the
// interactive scope selection UI and manifest generation.

import type { SlackScope, SlackScopeDefinition, SlackScopeCategory } from '../types/channel-config.js';

export const SLACK_SCOPE_REGISTRY: readonly SlackScopeDefinition[] = [
  // ── Reading ──────────────────────────────────────────────────────────────
  {
    scope: 'channels:read',
    name: 'Read Channels',
    description: 'View basic info about public channels in the workspace',
    category: 'reading',
    risk: 'low',
  },
  {
    scope: 'channels:history',
    name: 'Read Channel History',
    description: 'View messages and content in public channels the bot has been added to',
    category: 'reading',
    risk: 'medium',
  },
  {
    scope: 'app_mentions:read',
    name: 'Read App Mentions',
    description: 'View messages that directly mention the bot in conversations',
    category: 'reading',
    risk: 'low',
  },
  {
    scope: 'groups:read',
    name: 'Read Private Channels',
    description: 'View basic info about private channels the bot has been added to',
    category: 'reading',
    risk: 'medium',
  },
  {
    scope: 'groups:history',
    name: 'Read Private Channel History',
    description: 'View messages in private channels the bot has been added to',
    category: 'reading',
    risk: 'high',
  },
  {
    scope: 'im:read',
    name: 'Read Direct Messages',
    description: 'View basic info about direct messages with the bot',
    category: 'reading',
    risk: 'medium',
  },
  {
    scope: 'im:history',
    name: 'Read DM History',
    description: 'View messages in direct message conversations with the bot',
    category: 'reading',
    risk: 'high',
  },
  {
    scope: 'mpim:read',
    name: 'Read Group DMs',
    description: 'View basic info about group direct messages the bot is in',
    category: 'reading',
    risk: 'medium',
  },
  {
    scope: 'mpim:history',
    name: 'Read Group DM History',
    description: 'View messages in group direct messages the bot is in',
    category: 'reading',
    risk: 'high',
  },

  // ── Writing ──────────────────────────────────────────────────────────────
  {
    scope: 'assistant:write',
    name: 'Assistant Threads',
    description: 'Respond in assistant threads when users interact with the bot in Slack',
    category: 'writing',
    risk: 'low',
  },
  {
    scope: 'chat:write',
    name: 'Send Messages',
    description: 'Post messages in channels and conversations the bot is in',
    category: 'writing',
    risk: 'low',
  },
  {
    scope: 'chat:write.public',
    name: 'Send to Public Channels',
    description: 'Post messages in public channels without joining them',
    category: 'writing',
    risk: 'medium',
  },
  {
    scope: 'im:write',
    name: 'Send Direct Messages',
    description: 'Start direct message conversations with users',
    category: 'writing',
    risk: 'medium',
  },

  // ── Reactions ────────────────────────────────────────────────────────────
  {
    scope: 'reactions:read',
    name: 'Read Reactions',
    description: 'View emoji reactions on messages',
    category: 'reactions',
    risk: 'low',
  },
  {
    scope: 'reactions:write',
    name: 'Add Reactions',
    description: 'Add and remove emoji reactions on messages',
    category: 'reactions',
    risk: 'low',
  },

  // ── Users ────────────────────────────────────────────────────────────────
  {
    scope: 'users:read',
    name: 'Read Users',
    description: 'View users and their basic profile info in the workspace',
    category: 'users',
    risk: 'low',
  },
  {
    scope: 'users:read.email',
    name: 'Read User Emails',
    description: 'View email addresses of users in the workspace',
    category: 'users',
    risk: 'medium',
  },
  {
    scope: 'users.profile:write',
    name: 'Write Bot Profile',
    description: "Update the bot's own profile (status emoji + status text). Used to surface live/offline state to operators without polling.",
    category: 'users',
    risk: 'low',
    // ENG-4812: Slack rejects this scope under oauth_config.scopes.bot
    // with `illegal_bot_scopes`. It must be granted via a user token —
    // which is what `setBotStatus()` (calling users.profile.set in
    // packages/mcp/src/slack-channel.ts) actually requires anyway.
    token_type: 'user',
  },

  // ── Channel Management ───────────────────────────────────────────────────
  {
    scope: 'channels:join',
    name: 'Join Channels',
    description: 'Join public channels in the workspace',
    category: 'channel-management',
    risk: 'low',
  },
  {
    scope: 'channels:manage',
    name: 'Manage Channels',
    description: 'Create, archive, and manage public channels',
    category: 'channel-management',
    risk: 'high',
  },

  // ── Files ────────────────────────────────────────────────────────────────
  {
    scope: 'files:read',
    name: 'Read Files',
    description: 'View files shared in channels and conversations',
    category: 'files',
    risk: 'medium',
  },
  {
    scope: 'files:write',
    name: 'Upload Files',
    description: 'Upload, edit, and delete files',
    category: 'files',
    risk: 'medium',
  },

  // ── Pins ─────────────────────────────────────────────────────────────────
  {
    scope: 'pins:read',
    name: 'Read Pins',
    description: 'View pinned content in channels and conversations',
    category: 'pins',
    risk: 'low',
  },
  {
    scope: 'pins:write',
    name: 'Write Pins',
    description: 'Add and remove pinned messages and files',
    category: 'pins',
    risk: 'low',
  },

  // ── Emoji ───────────────────────────────────────────────────────────────
  {
    scope: 'emoji:read',
    name: 'Read Emoji',
    description: 'View custom emoji in the workspace',
    category: 'emoji',
    risk: 'low',
  },

  // ── Metadata & Other ────────────────────────────────────────────────────
  {
    scope: 'commands',
    name: 'Slash Commands',
    description: 'Add and handle slash commands',
    category: 'metadata',
    risk: 'low',
  },
  {
    scope: 'team:read',
    name: 'Read Workspace Info',
    description: 'View the name, domain, and icon of the workspace',
    category: 'metadata',
    risk: 'low',
  },
  {
    scope: 'team.preferences:read',
    name: 'Read Workspace Preferences',
    description: 'Read the preferences for workspaces the app has been installed to',
    category: 'metadata',
    risk: 'low',
  },
  {
    scope: 'metadata.message:read',
    name: 'Read Message Metadata',
    description: 'View metadata attached to messages',
    category: 'metadata',
    risk: 'low',
  },
] as const;

/** All categories in display order. */
export const SLACK_SCOPE_CATEGORIES: readonly SlackScopeCategory[] = [
  'reading',
  'writing',
  'reactions',
  'users',
  'channel-management',
  'files',
  'pins',
  'emoji',
  'metadata',
] as const;

/** Human-readable category labels. */
export const SLACK_SCOPE_CATEGORY_LABELS: Record<SlackScopeCategory, string> = {
  reading: 'Reading',
  writing: 'Writing',
  reactions: 'Reactions',
  users: 'Users',
  'channel-management': 'Channel Management',
  files: 'Files',
  pins: 'Pins',
  emoji: 'Emoji',
  metadata: 'Metadata & Other',
};

/** Default recommended scopes for a standard Slack bot. */
const DEFAULT_SCOPES: readonly SlackScope[] = [
  'app_mentions:read',
  'assistant:write',
  'channels:history',
  'channels:read',
  'chat:write',
  'commands',
  'emoji:read',
  'files:read',
  'files:write',
  'groups:history',
  'groups:read',
  'im:history',
  'im:read',
  'im:write',
  'mpim:history',
  'mpim:read',
  'reactions:read',
  'reactions:write',
  'users:read',
  'users.profile:write',
] as const;

/** Returns the recommended default set of Slack bot scopes. */
export function getDefaultSlackScopes(): SlackScope[] {
  return [...DEFAULT_SCOPES];
}

/** Returns scope definitions grouped by category. */
export function getScopesByCategory(): Map<SlackScopeCategory, SlackScopeDefinition[]> {
  const map = new Map<SlackScopeCategory, SlackScopeDefinition[]>();
  for (const cat of SLACK_SCOPE_CATEGORIES) {
    map.set(cat, []);
  }
  for (const def of SLACK_SCOPE_REGISTRY) {
    map.get(def.category)!.push(def);
  }
  return map;
}

/** Look up a scope definition by scope string. */
export function getSlackScopeDefinition(scope: SlackScope): SlackScopeDefinition | undefined {
  return SLACK_SCOPE_REGISTRY.find((s) => s.scope === scope);
}

/** Preset scope sets for CLI --preset flag. */
export const SLACK_SCOPE_PRESETS = {
  minimal: [
    'app_mentions:read',
    'chat:write',
  ] as SlackScope[],

  standard: [...DEFAULT_SCOPES] as SlackScope[],

  full: SLACK_SCOPE_REGISTRY.map((s) => s.scope) as SlackScope[],
} as const;
